import { Component, type ErrorInfo, type ReactNode } from "react";

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary so a single broken route doesn't strand the user
 * on the bare react-router fallback ("Hey developer 👋"). Renders a quiet
 * full-page card with a Reload button and logs the error to the console
 * with the location, so we can find the offending screen in production.
 */
export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (typeof console !== "undefined") {
      // Keep the stack — sourcemaps are inlined in the build so this resolves
      // straight to the offending .tsx in DevTools.
      console.error("[AppErrorBoundary]", error, "\nat", info.componentStack);
    }
  }

  reset = () => {
    this.setState({ error: null });
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    const { message } = this.state.error;
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fafafa] px-4 py-10">
        <div className="w-full max-w-[440px] rounded-[16px] border border-[#e2e8f0] bg-white p-6 shadow-sm">
          <h1 className="text-[20px] font-bold tracking-tight text-[#1a1a1a]">
            Something went wrong
          </h1>
          <p className="mt-2 text-[14px] text-[#475569]">
            We hit an unexpected error while rendering this screen. Reloading usually fixes it.
          </p>
          <pre className="mt-4 overflow-x-auto rounded-[8px] bg-[#fff1f2] p-3 text-[12px] leading-relaxed text-[#9f1239]">
            {message || "Unknown error"}
          </pre>
          <div className="mt-5 flex gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="inline-flex flex-1 items-center justify-center rounded-[10px] bg-[#2563eb] px-4 py-2.5 text-[14px] font-semibold text-white shadow-sm hover:bg-[#1d4ed8]"
            >
              Reload
            </button>
            <a
              href="/"
              className="inline-flex items-center justify-center rounded-[10px] border border-[#e2e8f0] bg-white px-4 py-2.5 text-[14px] font-semibold text-[#1a1a1a] no-underline hover:bg-[#f8fafc]"
            >
              Home
            </a>
          </div>
        </div>
      </div>
    );
  }
}
